/**
 * Admin page for managing showcase portfolio entries.
 *
 * Supports CRUD operations on showcase apps with image and video uploads.
 * Media is displayed in a preview slideshow within each entry.
 *
 * @module ShowcaseManagementPage
 */

import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  alpha,
  CircularProgress,
  Divider,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FloatingOrb } from '../components/AnimatedComponents';

interface ShowcaseMedia {
  url: string;
  type: 'image' | 'video';
  filename: string;
}

interface ShowcaseTranslation {
  name: string;
  description: string;
  short_description: string;
}

interface ShowcaseApp {
  id: string;
  name: string;
  description: string;
  short_description: string;
  translations: Record<string, ShowcaseTranslation>;
  platforms: string[];
  app_store_url?: string;
  play_store_url?: string;
  website_url?: string;
  screenshots: string[];
  media: ShowcaseMedia[];
  tags: string[];
  created_at: string;
}

const SUPPORTED_LANGUAGES = ['nl', 'en', 'de', 'fr', 'es'];

const emptyTranslation = (): ShowcaseTranslation => ({
  name: '',
  description: '',
  short_description: '',
});

interface AppFormData {
  name: string;
  description: string;
  short_description: string;
  translations: Record<string, ShowcaseTranslation>;
  platforms: string;
  app_store_url: string;
  play_store_url: string;
  website_url: string;
  tags: string;
}

const EMPTY_FORM: AppFormData = {
  name: '',
  description: '',
  short_description: '',
  translations: {},
  platforms: '',
  app_store_url: '',
  play_store_url: '',
  website_url: '',
  tags: '',
};

export default function ShowcaseManagementPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [apps, setApps] = useState<ShowcaseApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<AppFormData>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [langTab, setLangTab] = useState(0);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; appId: string; appName: string }>({
    open: false,
    appId: '',
    appName: '',
  });

  // Media upload state
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Slideshow state per app
  const [slideIndices, setSlideIndices] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/login');
      return;
    }
    fetchApps();
  }, [user, isAdmin, navigate]);

  const fetchApps = async () => {
    try {
      const res = await api.get('/api/showcase/');
      setApps(res.data);
    } catch {
      setMessage({ type: 'error', text: t('showcaseAdmin.fetchError') });
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setLangTab(0);
    setFormOpen(true);
  };

  const openEditDialog = (app: ShowcaseApp) => {
    setFormData({
      name: app.name,
      description: app.description,
      short_description: app.short_description,
      translations: app.translations || {},
      platforms: app.platforms.join(', '),
      app_store_url: app.app_store_url || '',
      play_store_url: app.play_store_url || '',
      website_url: app.website_url || '',
      tags: app.tags.join(', '),
    });
    setEditingId(app.id);
    setLangTab(0);
    setFormOpen(true);
  };

  const handleTranslationChange = (lang: string, field: keyof ShowcaseTranslation, value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...(prev.translations[lang] || emptyTranslation()),
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    // Filter out translations where all fields are empty
    const filteredTranslations: Record<string, ShowcaseTranslation> = {};
    for (const [lang, tr] of Object.entries(formData.translations)) {
      if (tr.name.trim() || tr.description.trim() || tr.short_description.trim()) {
        filteredTranslations[lang] = tr;
      }
    }
    const payload = {
      name: formData.name,
      description: formData.description,
      short_description: formData.short_description,
      translations: filteredTranslations,
      platforms: formData.platforms
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
      app_store_url: formData.app_store_url || null,
      play_store_url: formData.play_store_url || null,
      website_url: formData.website_url || null,
      tags: formData.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    };

    try {
      if (editingId) {
        await api.put(`/api/showcase/${editingId}`, payload);
        setMessage({ type: 'success', text: t('showcaseAdmin.updated') });
      } else {
        await api.post('/api/showcase/', payload);
        setMessage({ type: 'success', text: t('showcaseAdmin.created') });
      }
      setFormOpen(false);
      fetchApps();
    } catch {
      setMessage({ type: 'error', text: t('showcaseAdmin.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/showcase/${deleteDialog.appId}`);
      setMessage({ type: 'success', text: t('showcaseAdmin.deleted') });
      setDeleteDialog({ open: false, appId: '', appName: '' });
      fetchApps();
    } catch {
      setMessage({ type: 'error', text: t('showcaseAdmin.deleteFailed') });
    }
  };

  const handleMediaUpload = async (appId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadingFor(appId);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fd = new FormData();
      fd.append('file', file);
      try {
        await api.post(`/api/showcase/${appId}/media`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        successCount++;
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      setMessage({
        type: failCount > 0 ? 'error' : 'success',
        text: failCount > 0
          ? t('showcaseAdmin.uploadPartial', { success: successCount, fail: failCount })
          : t('showcaseAdmin.uploadSuccess', { count: successCount }),
      });
    } else {
      setMessage({ type: 'error', text: t('showcaseAdmin.uploadFailed') });
    }

    setUploading(false);
    setUploadingFor(null);
    fetchApps();
  };

  const handleDeleteMedia = async (appId: string, mediaUrl: string) => {
    try {
      await api.delete(`/api/showcase/${appId}/media`, { params: { media_url: mediaUrl } });
      setMessage({ type: 'success', text: t('showcaseAdmin.mediaRemoved') });
      fetchApps();
    } catch {
      setMessage({ type: 'error', text: t('showcaseAdmin.mediaRemoveFailed') });
    }
  };

  const getSlideIndex = (appId: string) => slideIndices[appId] || 0;

  const setSlideIndex = (appId: string, index: number) => {
    setSlideIndices((prev) => ({ ...prev, [appId]: index }));
  };

  if (!user || !isAdmin) return null;

  return (
    <PageTransition>
      <SEO title={t('showcaseAdmin.seoTitle')} description={t('showcaseAdmin.seoDesc')} noindex />
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          background: `linear-gradient(180deg, ${alpha(DARK_BLUE, 0.02)} 0%, transparent 40%)`,
        }}
      >
        <FloatingOrb size={250} color={GREEN} top={-60} right={-80} animationDelay="2s" />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <IconButton onClick={() => navigate('/admin')} sx={{ bgcolor: alpha(DARK_BLUE, 0.05) }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('showcaseAdmin.title')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                sx={{ ml: 'auto', borderRadius: 3, bgcolor: GREEN }}
              >
                {t('showcaseAdmin.addNew')}
              </Button>
            </Stack>
          </motion.div>

          <AnimatePresence>
            {message && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 3 }}>
                  {message.text}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : apps.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>{t('showcaseAdmin.empty')}</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} sx={{ borderRadius: 3, bgcolor: GREEN }}>
                {t('showcaseAdmin.addFirst')}
              </Button>
            </Box>
          ) : (
            <Stack spacing={3}>
              {apps.map((app, index) => {
                const allMedia: ShowcaseMedia[] = app.media && app.media.length > 0
                  ? app.media
                  : app.screenshots.map((url) => ({ url, type: 'image' as const, filename: '' }));
                const currentSlide = getSlideIndex(app.id);
                const currentMedia = allMedia[currentSlide];

                return (
                  <motion.div
                    key={app.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: index * 0.06 }}
                  >
                    <Card
                      sx={{
                        border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 30px ${alpha(DARK_BLUE, 0.08)}`,
                          borderColor: alpha(GREEN, 0.15),
                        },
                      }}
                    >
                      <CardContent>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>{app.translations?.en?.name || app.translations?.nl?.name || app.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{app.translations?.en?.short_description || app.translations?.nl?.short_description || app.short_description}</Typography>
                            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                              {app.platforms.map((p) => (
                                <Chip key={p} label={p} size="small" variant="outlined" />
                              ))}
                              {app.tags.map((tag) => (
                                <Chip key={tag} label={tag} size="small" color="secondary" />
                              ))}
                            </Stack>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title={t('showcaseAdmin.edit')}>
                              <IconButton onClick={() => openEditDialog(app)} sx={{ bgcolor: alpha(GREEN, 0.08) }}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('showcaseAdmin.delete')}>
                              <IconButton
                                onClick={() => setDeleteDialog({ open: true, appId: app.id, appName: app.name })}
                                sx={{ bgcolor: alpha('#f44336', 0.08) }}
                              >
                                <DeleteIcon color="error" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Media slideshow */}
                        {allMedia.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Box
                              sx={{
                                position: 'relative',
                                bgcolor: '#000',
                                borderRadius: 2,
                                overflow: 'hidden',
                                height: { xs: 200, md: 300 },
                              }}
                            >
                              {currentMedia?.type === 'video' ? (
                                <Box
                                  component="video"
                                  src={currentMedia.url}
                                  controls
                                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                              ) : (
                                <Box
                                  component="img"
                                  src={currentMedia?.url}
                                  alt={`Media ${currentSlide + 1}`}
                                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                              )}
                              {allMedia.length > 1 && (
                                <>
                                  <IconButton
                                    onClick={() => setSlideIndex(app.id, currentSlide > 0 ? currentSlide - 1 : allMedia.length - 1)}
                                    sx={{
                                      position: 'absolute',
                                      left: 8,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      bgcolor: 'rgba(255,255,255,0.9)',
                                      '&:hover': { bgcolor: 'white' },
                                    }}
                                    size="small"
                                  >
                                    <ArrowBackIosNewIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => setSlideIndex(app.id, currentSlide < allMedia.length - 1 ? currentSlide + 1 : 0)}
                                    sx={{
                                      position: 'absolute',
                                      right: 8,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      bgcolor: 'rgba(255,255,255,0.9)',
                                      '&:hover': { bgcolor: 'white' },
                                    }}
                                    size="small"
                                  >
                                    <ArrowForwardIosIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                              {/* Slide counter */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 8,
                                  bgcolor: 'rgba(0,0,0,0.6)',
                                  color: 'white',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                }}
                              >
                                {currentSlide + 1} / {allMedia.length}
                              </Box>
                            </Box>

                            {/* Thumbnail strip */}
                            <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto', pb: 1 }}>
                              {allMedia.map((m, idx) => (
                                <Box
                                  key={m.url}
                                  onClick={() => setSlideIndex(app.id, idx)}
                                  sx={{
                                    position: 'relative',
                                    width: 64,
                                    height: 48,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    border: idx === currentSlide ? `2px solid ${GREEN}` : '2px solid transparent',
                                    opacity: idx === currentSlide ? 1 : 0.6,
                                    transition: 'all 0.2s',
                                    flexShrink: 0,
                                    '&:hover': { opacity: 1 },
                                  }}
                                >
                                  {m.type === 'video' ? (
                                    <Box
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        bgcolor: alpha(DARK_BLUE, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <PlayCircleOutlineIcon sx={{ fontSize: 24, color: GREEN }} />
                                    </Box>
                                  ) : (
                                    <Box
                                      component="img"
                                      src={m.url}
                                      alt=""
                                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  )}
                                  {/* Delete overlay */}
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteMedia(app.id, m.url);
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: -4,
                                      right: -4,
                                      bgcolor: 'rgba(244,67,54,0.9)',
                                      color: 'white',
                                      width: 18,
                                      height: 18,
                                      '&:hover': { bgcolor: '#f44336' },
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 12 }} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Upload button */}
                        <Button
                          variant="outlined"
                          startIcon={uploading && uploadingFor === app.id ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                          disabled={uploading && uploadingFor === app.id}
                          onClick={() => {
                            setUploadingFor(app.id);
                            fileInputRef.current?.click();
                          }}
                          sx={{ borderRadius: 3 }}
                        >
                          {t('showcaseAdmin.uploadMedia')}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </Stack>
          )}
        </Container>

        {/* Hidden file input for media uploads */}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
          onChange={(e) => {
            if (uploadingFor) {
              handleMediaUpload(uploadingFor, e.target.files);
            }
            e.target.value = '';
          }}
        />

        {/* Create / Edit Dialog */}
        <Dialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>
            {editingId ? t('showcaseAdmin.editTitle') : t('showcaseAdmin.createTitle')}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {/* Language tabs for translated fields */}
              <Tabs
                value={langTab}
                onChange={(_, v) => setLangTab(v)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {SUPPORTED_LANGUAGES.map((lang) => {
                  const tr = formData.translations[lang];
                  const hasContent = tr && (tr.name.trim() || tr.description.trim() || tr.short_description.trim());
                  return (
                    <Tab
                      key={lang}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {lang.toUpperCase()}
                          {hasContent && (
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: GREEN }} />
                          )}
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
              {SUPPORTED_LANGUAGES.map((lang, idx) => (
                <Box key={lang} sx={{ display: langTab === idx ? 'block' : 'none' }}>
                  <Stack spacing={2}>
                    <TextField
                      label={t('showcaseAdmin.fieldName')}
                      value={formData.translations[lang]?.name || ''}
                      onChange={(e) => handleTranslationChange(lang, 'name', e.target.value)}
                      fullWidth
                      required={lang === 'nl'}
                    />
                    <TextField
                      label={t('showcaseAdmin.fieldShortDesc')}
                      value={formData.translations[lang]?.short_description || ''}
                      onChange={(e) => handleTranslationChange(lang, 'short_description', e.target.value)}
                      fullWidth
                      required={lang === 'nl'}
                      inputProps={{ maxLength: 300 }}
                      helperText={`${(formData.translations[lang]?.short_description || '').length}/300`}
                    />
                    <TextField
                      label={t('showcaseAdmin.fieldDescription')}
                      value={formData.translations[lang]?.description || ''}
                      onChange={(e) => handleTranslationChange(lang, 'description', e.target.value)}
                      fullWidth
                      required={lang === 'nl'}
                      multiline
                      rows={4}
                      inputProps={{ maxLength: 5000 }}
                    />
                  </Stack>
                </Box>
              ))}
              <TextField
                label={t('showcaseAdmin.fieldPlatforms')}
                value={formData.platforms}
                onChange={(e) => setFormData((prev) => ({ ...prev, platforms: e.target.value }))}
                fullWidth
                helperText={t('showcaseAdmin.platformsHelp')}
              />
              <TextField
                label={t('showcaseAdmin.fieldTags')}
                value={formData.tags}
                onChange={(e) => setFormData((prev) => ({ ...prev, tags: e.target.value }))}
                fullWidth
                helperText={t('showcaseAdmin.tagsHelp')}
              />
              <TextField
                label={t('showcaseAdmin.fieldAppStore')}
                value={formData.app_store_url}
                onChange={(e) => setFormData((prev) => ({ ...prev, app_store_url: e.target.value }))}
                fullWidth
                placeholder="https://apps.apple.com/..."
              />
              <TextField
                label={t('showcaseAdmin.fieldPlayStore')}
                value={formData.play_store_url}
                onChange={(e) => setFormData((prev) => ({ ...prev, play_store_url: e.target.value }))}
                fullWidth
                placeholder="https://play.google.com/..."
              />
              <TextField
                label={t('showcaseAdmin.fieldWebsite')}
                value={formData.website_url}
                onChange={(e) => setFormData((prev) => ({ ...prev, website_url: e.target.value }))}
                fullWidth
                placeholder="https://..."
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setFormOpen(false)} sx={{ borderRadius: 3 }}>
              {t('showcaseAdmin.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !Object.values(formData.translations).some((tr) => tr.name.trim() && tr.description.trim() && tr.short_description.trim())}
              sx={{ borderRadius: 3, bgcolor: GREEN }}
            >
              {saving ? <CircularProgress size={20} /> : editingId ? t('showcaseAdmin.save') : t('showcaseAdmin.create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, appId: '', appName: '' })}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>{t('showcaseAdmin.deleteConfirmTitle')}</DialogTitle>
          <DialogContent>
            <Typography>{t('showcaseAdmin.deleteConfirmText', { name: deleteDialog.appName })}</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteDialog({ open: false, appId: '', appName: '' })} sx={{ borderRadius: 3 }}>
              {t('showcaseAdmin.cancel')}
            </Button>
            <Button onClick={handleDelete} variant="contained" color="error" sx={{ borderRadius: 3 }}>
              {t('showcaseAdmin.confirmDelete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageTransition>
  );
}
